import { API_V1 } from 'API/APIs/APIv1';
import { LinkedinTypes } from 'enums';
import { EmailAccountTypes, EmailCredentials, OAuthCredentials, EmailAccount } from 'features/co-pilot/linkedin-accounts/linkedin-accounts.types';
import _ from 'lodash';
import Logger from 'logger';
import type { License } from 'types/licenses.types';

export type LinkedinTwoFaVerificationInput =
  | string
  | {
      verificationPIN?: string;
      action?: 'approved';
    };

export const getAllLinkedinAccounts = async (signal?: AbortSignal) => {
  try {
    const response = await API_V1.get('/linkedin-accounts/all', {
      signal,
    });

    return response.data;
  } catch (e) {
    Logger.trace('Error while getting all linkedin accounts!');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const getCountryCodes = async () => {
  try {
    const response = await API_V1.get('/linkedin-accounts/country-codes');

    return response.data;
  } catch (e) {
    Logger.trace('Error while getting country codes!');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const getLinkedinAccountById = async (accountId: string) => {
  try {
    const response = await API_V1.get(`/linkedin-accounts/${accountId}/details`);

    return response.data;
  } catch (e) {
    Logger.trace('Error while getting linkedin account %s!', accountId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const createLinkedinAccount = async (
  countryName: string,
  cityName: string,
  userEmail: string,
  userPassword: string,
  liProductType: LinkedinTypes,
  contractName?: string,
  /**
   * Der im ersten Schritt gewählte Sitz. Geht mit dem Anlegen zusammen raus, statt danach
   * per eigenem Aufruf verknüpft zu werden — der konnte fehlschlagen, und übrig blieb ein
   * Konto ohne Lizenz.
   */
  licenseId?: string,
) => {
  try {
    const response = await API_V1.post('/linkedin-accounts/add', {
      countryName,
      cityName,
      userEmail,
      userPassword,
      liProductType,
      contractName,
      licenseId,
    });

    return response.data;
  } catch (e) {
    Logger.trace('Error while creating a new linkedin account!');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const syncLinkedinAccount = async (accountId: string, priority?: 'high' | 'low') => {
  try {
    const response = await API_V1.post(`/linkedin-accounts/${accountId}/sync`, {
      priority,
    });

    return response.data;
  } catch (e) {
    Logger.trace('Error while syncing the linkedin account %s!', accountId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const triggerAccountVerification = async (accountId: string, priority?: 'high' | 'low') => {
  try {
    // Prefer the active sync/login-attempt endpoint. Keep fallback for older backends.
    const response = await API_V1.post(`/linkedin-accounts/${accountId}/sync`, {
      priority,
    });
    return response.data;
  } catch (e) {
    const status = _.get(e, 'response.status');
    if (status === 404 || status === 405) {
      try {
        const fallbackResponse = await API_V1.post(`/linkedin-accounts/${accountId}/trigger-verification`, {
          priority,
        });
        return fallbackResponse.data;
      } catch (fallbackError) {
        Logger.trace('Error while checking the linkedin account %s login status!', accountId);
        Logger.error(fallbackError);
        return { error: _.get(fallbackError, 'response.data', fallbackError), data: [] };
      }
    }

    Logger.trace('Error while checking the linkedin account %s login status!', accountId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const logoutLinkedinAccount = async (
  accountId: string,
  stayLoggedOutTill: number = 0
): Promise<any> => {
  try {
    const payload: { stayLoggedOutTill?: number } = {};

    if (stayLoggedOutTill > 0) {
      payload.stayLoggedOutTill = stayLoggedOutTill;
    }

    const response = await API_V1.post(`/linkedin-accounts/${accountId}/logout`, payload);

    Logger.info(`Successfully logged out LinkedIn account with ID: ${accountId}`);

    return response.data;
  } catch (e) {
    const errorMessage = _.get(e, 'response.data.message', 'Unknown error');

    Logger.trace(`Error while logging out the LinkedIn account ${accountId}!`);
    Logger.error(e);

    return { error: errorMessage, data: [] };
  }
};

export const resetLinkedinAccountLocalStorage = async (accountId: string) => {
  try {
    const response = await API_V1.post(`/linkedin-accounts/${accountId}/reset-local-storage`, {});

    return response.data;
  } catch (e) {
    Logger.trace('Error while resetting local storage for linkedin account %s!', accountId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const resetLinkedinAccount = async (accountId: string) => {
  try {
    const response = await API_V1.post(`/linkedin-accounts/${accountId}/reset`, {});
    return response.data;
  } catch (e) {
    Logger.trace('Error while resetting linkedin account %s!', accountId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const verifyLinkedinAccount = async (accountId: string, verificationInput: LinkedinTwoFaVerificationInput) => {
  try {
    const payload =
      typeof verificationInput === 'string'
        ? { verificationPIN: verificationInput }
        : verificationInput;

    const response = await API_V1.put(`/linkedin-accounts/${accountId}/pin`, payload);

    return response.data;
  } catch (e) {
    Logger.trace('Error while verifying the linkedin account %s!', accountId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const updateLinkedinAccount = async (
  accountId: string,
  countryName: string,
  cityName: string,
  userEmail: string,
  userPassword: string,
  liProductType: LinkedinTypes
) => {
  try {
    const response = await API_V1.put(`/linkedin-accounts/${accountId}/legacy-update`, {
      countryName,
      cityName,
      userEmail,
      userPassword,
      liProductType,
    });

    return response.data;
  } catch (e) {
    Logger.trace('Error while updating linkedin account %s!', accountId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const updateLinkedinAccountWithPayload = async (
  accountId: string,
  payload: {
    countryName?: string;
    cityName?: string;
    userEmail?: string;
    userPassword?: string;
    liProductType?: LinkedinTypes;
  }
) => {
  try {
    const response = await API_V1.put(`/linkedin-accounts/${accountId}/legacy-update`, payload);

    return response.data;
  } catch (e) {
    Logger.trace('Error while updating linkedin account %s!', accountId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const updateLinkedinAccountNames = async (accountId: string, firstName?: string, lastName?: string) => {
  try {
    const response = await API_V1.put(`/linkedin-accounts/${accountId}/legacy-update`, { firstName, lastName });

    return response.data;
  } catch (e) {
    Logger.trace('Error while updating linkedin account %s names!', accountId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const updateLinkedinAccountContract = async (accountId: string, contractName: string) => {
  try {
    const response = await API_V1.put(`/linkedin-accounts/${accountId}/legacy-update`, { contractName });

    return response.data;
  } catch (e) {
    Logger.trace('Error while updating linkedin account %s contract!', accountId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const deleteLinkedinAccount = async (accountId: string) => {
  try {
    const response = await API_V1.delete(`/linkedin-accounts/${accountId}/with-unlink`);

    return response.data ?? { success: true };
  } catch (e) {
    Logger.trace('Error while deleting linkedin account %s!', accountId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const updateLinkedinAccountEmailCredentials = async (
  accountId: string,
  type: EmailAccountTypes,
  smtp?: EmailCredentials,
  imap?: EmailCredentials,
  oauth?: OAuthCredentials
) => {
  try {
    const smtpPayload = !_.isNil(smtp)
      ? {
          server: smtp.server,
          port: +smtp.port,
          encryption: smtp.encryption,
          username: smtp.username,
          password: smtp.password,
        }
      : undefined;

    const imapPayload = !_.isNil(imap)
      ? {
          server: imap.server,
          port: +imap.port,
          encryption: imap.encryption,
          username: smtp?.username ?? '',
          password: smtp?.password ?? '',
        }
      : undefined;

    const oauthPayload = !_.isNil(oauth)
      ? {
          email: oauth.email,
          code: oauth.code,
        }
      : undefined;

    const response = await API_V1.post(`/linkedin-accounts/${accountId}/email-config`, {
      type,
      smtp: smtpPayload,
      imap: imapPayload,
      oauth: oauthPayload,
    });

    return response.data;
  } catch (e) {
    Logger.trace('Error while updating linkedin account %s email credentials!', accountId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const getCaptcha = async (accountId: string) => {
  try {
    const response = await API_V1.get(`/linkedin-accounts/${accountId}/captcha`, {
      responseType: 'arraybuffer',
    });

    return response.data;
  } catch (e) {
    Logger.trace('Error while getting captcha for linkedin account %s!', accountId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const solveCaptcha = async (accountId: string, positions: { y: number; x: number }) => {
  try {
    const response = await API_V1.put(`/linkedin-accounts/${accountId}/solve-captcha`, {
      positions,
    });

    return response.data;
  } catch (e) {
    Logger.trace('Error while solving captcha for linkedin account %s!', accountId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const getThirdPartyScreenshot = async (accountId: string) => {
  try {
    const response = await API_V1.get(`/linkedin-accounts/${accountId}/third-party-screenshot`, {
      responseType: 'arraybuffer',
    });

    return response.data;
  } catch (e) {
    Logger.trace('Error while getting third party screenshot for linkedin account %s!', accountId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const thirdPartyClick = async (accountId: string, positions: { y: number; x: number }) => {
  try {
    const response = await API_V1.put(`/linkedin-accounts/${accountId}/third-party-click`, {
      positions,
    });

    return response.data;
  } catch (e) {
    Logger.trace('Error while clicking in third party page for linkedin account %s!', accountId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const setThirdPartyText = async (accountId: string, thirdPartyText: string) => {
  try {
    const response = await API_V1.put(`/linkedin-accounts/${accountId}/third-party-text`, {
      thirdPartyText,
    });

    return response.data;
  } catch (e) {
    Logger.trace('Error while verifying the linkedin account %s!', accountId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const getShareableToken = async () => {
  try {
    const response = await API_V1.get('/shareable-token');

    return response.data;
  } catch (e) {
    Logger.trace('Error while fetching shareable token!');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const updateLinkedinAccountSignature = async (accountId: string, signature: string) => {
  try {
    const response = await API_V1.put(`/linkedin-accounts/${accountId}/legacy-update`, { signature });

    return response.data;
  } catch (e) {
    Logger.trace('Error while updating linkedin account %s signature!', accountId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

/** -------------------------------------------------------------
 * E-MAIL ACCOUNTS (multiple senders per LinkedIn account)
 * --------------------------------------------------------------*/

/**
 * GET /linkedin-accounts/:liId/emailAccounts
 */
export const getEmailAccounts = async (accountId: string, signal?: AbortSignal) => {
  try {
    const response = await API_V1.get(`/linkedin-accounts/${accountId}/email-accounts`, {
      signal,
    });
    return response.data as EmailAccount[];
  } catch (e) {
    Logger.trace('Error while getting email accounts for linkedin account %s!', accountId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

/**
 * POST /linkedin-accounts/:liId/emailAccounts
 */
export const createEmailAccount = async (
  accountId: string,
  payload: {
    type: 'smtp' | 'ms_o365' | 'gmail';
    smtp?: EmailCredentials & { imap?: EmailCredentials };
    oauth?: OAuthCredentials;
    gmailOauth?: OAuthCredentials;
  }
) => {
  try {
    const response = await API_V1.post(`/linkedin-accounts/${accountId}/email-accounts`, payload);
    return response.data;
  } catch (e) {
    Logger.trace('Error while creating email account for linkedin account %s!', accountId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

/**
 * PUT /linkedin-accounts/:liId/emailAccounts/:eaId
 */
export const updateEmailAccount = async (
  accountId: string,
  emailAccountId: string,
  payload: Partial<{
    type: 'smtp' | 'ms_o365' | 'gmail';
    smtp: EmailCredentials & { imap?: EmailCredentials };
    oauth: OAuthCredentials;
    gmailOauth: OAuthCredentials;
    dailyLimit: number;
  }>
) => {
  try {
    const response = await API_V1.put(`/linkedin-accounts/${accountId}/email-accounts/${emailAccountId}`, payload);
    return response.data;
  } catch (e) {
    Logger.trace('Error while updating email account %s for linkedin account %s!', emailAccountId, accountId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

/**
 * DELETE /linkedin-accounts/:liId/emailAccounts/:eaId
 */
export const deleteEmailAccount = async (accountId: string, emailAccountId: string) => {
  try {
    const response = await API_V1.delete(`/linkedin-accounts/${accountId}/email-accounts/${emailAccountId}`);
    return response.data ?? { success: true };
  } catch (e) {
    Logger.trace('Error while deleting email account %s for linkedin account %s!', emailAccountId, accountId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

// ─── SAIA License Linking ────────────────────────────────

/**
 * GET /licenses/unlinked - Get SAIA licenses not linked to any LinkedIn account
 */
export const getUnlinkedSaiaLicenses = async (): Promise<License[] | { error: unknown }> => {
  try {
    const response = await API_V1.get('/licenses/unlinked');
    return response.data;
  } catch (e) {
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

/**
 * PATCH /licenses/:licenseId/link - Link a license to a LinkedIn account
 */
export const linkLicenseToAccount = async (licenseId: string, linkedinAccountId: string) => {
  try {
    const response = await API_V1.patch(`/licenses/${licenseId}/link`, { linkedinAccountId });
    return response.data;
  } catch (e) {
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

/**
 * PATCH /licenses/:licenseId/unlink - Unlink a license from its LinkedIn account
 */
export const unlinkLicenseFromAccount = async (licenseId: string) => {
  try {
    const response = await API_V1.patch(`/licenses/${licenseId}/unlink`);
    return response.data;
  } catch (e) {
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export interface LinkedinConnectionItem {
  _id: string;
  profileId?: string;
  vanityName?: string;
  linkedinUrl?: string;
  firstSeenAt: string;
  lastSeenAt: string;
  contact?: { _id: string; firstName?: string; lastName?: string; headline?: string; company?: string } | null;
}

export interface LinkedinConnectionsResponse {
  items: LinkedinConnectionItem[];
  total: number;
  lastCrawlAt: string | null;
  fullCrawlAt: string | null;
  refresh: { requestedPages: number; nextPage: number } | null;
}

/** Kontaktliste (Verbindungen ersten Grades) eines Kontos aus dem gespeicherten Bestand. */
export const getLinkedinAccountConnections = async (
  accountId: string,
  params: { q?: string; limit?: number; offset?: number },
  signal?: AbortSignal,
) => {
  try {
    const response = await API_V1.get<LinkedinConnectionsResponse>(`/linkedin-accounts/${accountId}/connections`, { params, signal });
    return response.data;
  } catch (e) {
    Logger.trace('Error while loading connections for linkedin account %s!', accountId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) } as { error: unknown };
  }
};

/** Vollständiges Neueinlesen der Kontaktliste anfordern. */
export const requestLinkedinAccountConnectionsRefresh = async (accountId: string) => {
  try {
    const response = await API_V1.post(`/linkedin-accounts/${accountId}/connections/refresh`, {});
    return response.data;
  } catch (e) {
    Logger.trace('Error while requesting connections refresh for linkedin account %s!', accountId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};
